import { useState } from "react";
import { FaCopy } from "react-icons/fa";
import { useTheme } from "../../../Theme/ThemeContext";

const TheoryView = ({ topic }) => {
  const { currentTheme } = useTheme();
  const [copiedKey, setCopiedKey] = useState(null);

  const copyToClipboard = async (value) => {
    const text = String(value ?? "");
    if (!text) return false;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // fall through to legacy copy
    }

    try {
      const el = document.createElement("textarea");
      el.value = text;
      el.setAttribute("readonly", "");
      el.style.position = "fixed";
      el.style.left = "-9999px";
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(el);
      return ok;
    } catch {
      return false;
    }
  };
  const tokenizeTheoryContent = (raw) => {
    const content = String(raw ?? "").replace(/\r/g, "");
    const lines = content.split("\n");

    const blocks = [];

    const flushText = (textLines) => {
      const value = textLines.join("\n").trimEnd();
      if (value.trim()) blocks.push({ type: "text", value });
    };

    const flushUl = (items) => {
      const normalized = items.map((s) => String(s).trim()).filter(Boolean);
      if (normalized.length) blocks.push({ type: "ul", items: normalized });
    };

    let textBuffer = [];
    let ulBuffer = [];

    let isInGoBlock = false;
    let goLines = [];

    const consumeLine = (line) => {
      const trimmed = String(line ?? "");
      const t = trimmed.trim();

      if (!t) {
        // blank line breaks UL/text blocks
        if (ulBuffer.length) {
          flushUl(ulBuffer);
          ulBuffer = [];
        }
        if (textBuffer.length) {
          flushText(textBuffer);
          textBuffer = [];
        }
        return;
      }

      if (t.startsWith("img:")) {
        if (ulBuffer.length) {
          flushUl(ulBuffer);
          ulBuffer = [];
        }
        if (textBuffer.length) {
          flushText(textBuffer);
          textBuffer = [];
        }
        blocks.push({ type: "img", value: t.slice(4).trim() });
        return;
      }

      if (t.startsWith("table:") && t.endsWith(":table")) {
        if (ulBuffer.length) {
          flushUl(ulBuffer);
          ulBuffer = [];
        }
        if (textBuffer.length) {
          flushText(textBuffer);
          textBuffer = [];
        }
        try {
          const json = t.slice(6, -6).trim();
          const rows = JSON.parse(json);
          // Only push if rows is an array of arrays
          if (Array.isArray(rows) && Array.isArray(rows[0])) {
            blocks.push({ type: "table", rows });
          }
        } catch {
          // If parsing fails, treat as text or ignore
          // For now, ignore invalid table block
        }
        return;
      }

      if (t.startsWith("head:") && t.includes(":head")) {
        if (ulBuffer.length) {
          flushUl(ulBuffer);
          ulBuffer = [];
        }
        if (textBuffer.length) {
          flushText(textBuffer);
          textBuffer = [];
        }
        const heading = t.replace(/^head:/, "").replace(/:head$/, "").trim();
        blocks.push({ type: "head", value: heading });
        return;
      }

      if (t.startsWith("ul:") && t.includes(":ul")) {
        if (textBuffer.length) {
          flushText(textBuffer);
          textBuffer = [];
        }
        const item = t.replace(/^ul:/, "").replace(/:ul$/, "").trim();
        ulBuffer.push(item);
        return;
      }

      // Inline go:...:go on the same line
      if (t.startsWith("go:") && t.endsWith(":go")) {
        if (ulBuffer.length) {
          flushUl(ulBuffer);
          ulBuffer = [];
        }
        if (textBuffer.length) {
          flushText(textBuffer);
          textBuffer = [];
        }
        const code = t.slice(3, -3).trimStart();
        blocks.push({ type: "go", value: code });
        return;
      }

      // Multiline go block: starts with go: and ends when we hit a line ending with :go
      if (t.startsWith("go:")) {
        if (ulBuffer.length) {
          flushUl(ulBuffer);
          ulBuffer = [];
        }
        if (textBuffer.length) {
          flushText(textBuffer);
          textBuffer = [];
        }
        isInGoBlock = true;
        goLines.push(t.slice(3));
        return;
      }

      // default text line
      if (ulBuffer.length) {
        flushUl(ulBuffer);
        ulBuffer = [];
      }
      textBuffer.push(trimmed);
    };

    for (const line of lines) {
      const t = String(line ?? "");

      if (!isInGoBlock) {
        consumeLine(t);
        continue;
      }

      // inside go block
      if (t.trimEnd().endsWith(":go")) {
        const withoutSuffix = t.replace(/:go\s*$/, "");
        goLines.push(withoutSuffix);
        const code = goLines.join("\n").replace(/^\n/, "");
        blocks.push({ type: "go", value: code });
        isInGoBlock = false;
        goLines = [];
      } else {
        goLines.push(t);
      }
    }

    if (ulBuffer.length) flushUl(ulBuffer);
    if (textBuffer.length) flushText(textBuffer);
    if (isInGoBlock && goLines.length) {
      blocks.push({ type: "go", value: goLines.join("\n") });
    }

    return blocks;
  };

  const blocks = (topic?.theoryContent || []).flatMap((c, i) =>
    tokenizeTheoryContent(c).map((b, j) => ({ ...b, _key: `${i}-${j}` }))
  );

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header Card */}
      <div className="mb-8 p-6 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl shadow-lg">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-white/20 rounded-xl">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Theory Lesson</h1>
            <p className="text-purple-200 text-sm">Read through the content below</p>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 space-y-8">
        {blocks.length > 0 ? (
          blocks.map((block) => {
            if (block.type === "img") {
              const imageUrl = block.value;
              return (
                <div key={block._key} className="flex justify-center my-8">
                  <div className="rounded-xl overflow-hidden shadow-lg border border-gray-100">
                    <img
                      src={imageUrl}
                      alt="Theory content"
                      className="max-w-full h-auto"
                    />
                  </div>
                </div>
              );
            }

            if (block.type === "head") {
              return (
                <div key={block._key} className="pt-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-1.5 h-8 bg-gradient-to-b ${currentTheme.colors.gradient} rounded-full`} />
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                      {block.value}
                    </h2>
                  </div>
                </div>
              );
            }

            if (block.type === "ul") {
              return (
                <div key={block._key} className="pl-6">
                  {block.items && block.items.length > 0 ? (
                    <ul className="space-y-3">
                      {block.items.map((item, i) => (
                        <li key={`${block._key}-${i}`} className="flex items-start gap-3">
                          <span className="mt-2 w-2 h-2 bg-theme-primary rounded-full flex-shrink-0" />
                          <span className="text-gray-700 leading-relaxed text-base whitespace-pre-wrap">
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              );
            }

            if (block.type === "table") {
              const rows = block.rows;
              return (
                <div key={block._key} className="my-6 overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                  <table className="w-full text-left text-sm text-gray-700">
                    <thead className="bg-gray-50 text-gray-900 font-semibold border-b border-gray-200">
                      <tr>
                        {rows[0].map((header, idx) => (
                          <th key={idx} className="px-6 py-3 whitespace-nowrap">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {rows.slice(1).map((row, rIdx) => (
                        <tr key={rIdx} className="hover:bg-gray-50/50">
                          {row.map((cell, cIdx) => (
                            <td key={cIdx} className="px-6 py-4">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            }

            if (block.type === "go") {
              return (
                <div key={block._key} className="my-6">
                  <div className="rounded-xl overflow-hidden shadow-lg border border-gray-800">
                    <div className="px-4 py-3 bg-gray-800 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1.5">
                          <div className="w-3 h-3 rounded-full bg-red-500" />
                          <div className="w-3 h-3 rounded-full bg-yellow-500" />
                          <div className="w-3 h-3 rounded-full bg-green-500" />
                        </div>
                        <span className="ml-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Go</span>
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          const ok = await copyToClipboard(block.value);
                          if (!ok) return;
                          setCopiedKey(block._key);
                          setTimeout(() => setCopiedKey(null), 1500);
                        }}
                        className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg transition-colors text-xs font-medium"
                        title="Copy code"
                      >
                        <FaCopy size={12} />
                        {copiedKey === block._key ? "Copied!" : "Copy"}
                      </button>
                    </div>
                    <pre className="p-5 bg-gray-900 overflow-x-auto text-sm leading-relaxed">
                      <code className="text-green-400 font-mono">{block.value}</code>
                    </pre>
                  </div>
                </div>
              );
            }

            return (
              <div key={block._key} className="pl-6">
                <p className="text-gray-700 leading-relaxed text-base whitespace-pre-wrap">
                  {block.value}
                </p>
              </div>
            );
          })
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-gray-500 font-medium">No content available</p>
            <p className="text-gray-400 text-sm mt-1">This lesson doesn't have any content yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TheoryView;
